/**
 * Code Quality Analyzer
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Deep static analysis of AI-generated code
 * - Complexity metrics and maintainability scores
 * - Security vulnerability detection
 * - Best practices enforcement
 * - Code smell detection
 */

import * as fs from 'fs';
import * as path from 'path';

export interface QualityMetrics {
  complexity: ComplexityMetrics;
  maintainability: MaintainabilityMetrics;
  security: SecurityMetrics;
  bestPractices: BestPracticesMetrics;
  codeSmells: CodeSmell[];
  overallScore: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
}

export interface ComplexityMetrics {
  cyclomaticComplexity: number;
  cognitiveComplexity: number;
  nestingDepth: number;
  linesOfCode: number;
  functionsCount: number;
  avgFunctionLength: number;
  score: number;
}

export interface MaintainabilityMetrics {
  commentRatio: number;
  namingQuality: number;
  modularity: number;
  codeReuse: number;
  score: number;
}

export interface SecurityMetrics {
  vulnerabilities: SecurityVulnerability[];
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  score: number;
}

export interface SecurityVulnerability {
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  line: number;
  message: string;
  fix?: string;
}

export interface BestPracticesMetrics {
  violations: BestPracticeViolation[];
  adherenceScore: number;
  score: number;
}

export interface BestPracticeViolation {
  rule: string;
  severity: 'info' | 'warning' | 'error';
  line: number;
  message: string;
  fix?: string;
}

export interface CodeSmell {
  type: string;
  severity: 'minor' | 'major' | 'critical';
  location: { start: number; end: number };
  description: string;
  suggestion: string;
}

export class CodeQualityAnalyzer {
  private workspaceRoot: string;

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
  }

  /**
   * Analyze code quality
   */
  analyze(code: string, language: string = 'typescript'): QualityMetrics {
    const complexity = this.analyzeComplexity(code);
    const maintainability = this.analyzeMaintainability(code);
    const security = this.analyzeSecurity(code);
    const bestPractices = this.analyzeBestPractices(code, language);
    const codeSmells = this.detectCodeSmells(code);

    // Calculate overall score
    const overallScore = Math.round(
      complexity.score * 0.25 +
      maintainability.score * 0.25 +
      security.score * 0.30 +
      bestPractices.score * 0.20
    );

    const grade = this.calculateGrade(overallScore);

    return {
      complexity,
      maintainability,
      security,
      bestPractices,
      codeSmells,
      overallScore,
      grade,
    };
  }

  /**
   * Analyze code complexity
   */
  private analyzeComplexity(code: string): ComplexityMetrics {
    const lines = code.split('\n');
    const linesOfCode = lines.filter(l => l.trim() && !l.trim().startsWith('//')).length;

    // Count decision points for cyclomatic complexity
    const decisionPatterns = [
      /\bif\s*\(/g,
      /\belse\s+if\s*\(/g,
      /\bfor\s*\(/g,
      /\bwhile\s*\(/g,
      /\bswitch\s*\(/g,
      /\bcase\s+/g,
      /\bcatch\s*\(/g,
      /\?\s*[^:]+\s*:/g, // Ternary
      /&&/g,
      /\|\|/g,
    ];

    let cyclomaticComplexity = 1; // Base complexity
    for (const pattern of decisionPatterns) {
      const matches = code.match(pattern);
      if (matches) {
        cyclomaticComplexity += matches.length;
      }
    }

    // Calculate nesting depth
    let maxNestingDepth = 0;
    let currentDepth = 0;
    for (const char of code) {
      if (char === '{') {
        currentDepth++;
        maxNestingDepth = Math.max(maxNestingDepth, currentDepth);
      } else if (char === '}') {
        currentDepth = Math.max(0, currentDepth - 1);
      }
    }

    // Count functions
    const functionPatterns = [
      /function\s+\w+\s*\(/g,
      /const\s+\w+\s*=\s*(?:async\s*)?\(/g,
      /const\s+\w+\s*=\s*(?:async\s*)?\([^)]*\)\s*=>/g,
      /\w+\s*\([^)]*\)\s*\{/g,
    ];

    let functionsCount = 0;
    for (const pattern of functionPatterns) {
      const matches = code.match(pattern);
      if (matches) {
        functionsCount += matches.length;
      }
    }
    functionsCount = Math.max(1, functionsCount / 2); // Rough dedup

    const avgFunctionLength = functionsCount > 0 ? linesOfCode / functionsCount : linesOfCode;

    // Calculate cognitive complexity (simplified)
    let cognitiveComplexity = cyclomaticComplexity;
    cognitiveComplexity += maxNestingDepth * 2; // Nesting adds cognitive load
    
    // Penalize deeply nested code
    if (maxNestingDepth > 4) {
      cognitiveComplexity += (maxNestingDepth - 4) * 3;
    }

    // Calculate score (lower complexity = higher score)
    let score = 100;
    score -= Math.min(40, cyclomaticComplexity * 2);
    score -= Math.min(20, maxNestingDepth * 4);
    score -= Math.min(20, Math.max(0, avgFunctionLength - 20));
    score = Math.max(0, score);

    return {
      cyclomaticComplexity,
      cognitiveComplexity,
      nestingDepth: maxNestingDepth,
      linesOfCode,
      functionsCount: Math.round(functionsCount),
      avgFunctionLength: Math.round(avgFunctionLength),
      score,
    };
  }

  /**
   * Analyze maintainability
   */
  private analyzeMaintainability(code: string): MaintainabilityMetrics {
    const lines = code.split('\n');
    const totalLines = lines.length;

    // Comment ratio
    const commentLines = lines.filter(l => 
      l.trim().startsWith('//') || 
      l.trim().startsWith('/*') || 
      l.trim().startsWith('*')
    ).length;
    const commentRatio = totalLines > 0 ? commentLines / totalLines : 0;

    // Naming quality (check for descriptive names)
    const identifiers = code.match(/(?:const|let|var|function)\s+([a-zA-Z_]\w*)/g) || [];
    let goodNames = 0;
    for (const id of identifiers) {
      const name = id.split(/\s+/).pop() || '';
      // Good names are descriptive (length > 2, not single letters except i,j,k,x,y)
      if (name.length > 2 || ['i', 'j', 'k', 'x', 'y', 'id'].includes(name)) {
        goodNames++;
      }
    }
    const namingQuality = identifiers.length > 0 ? goodNames / identifiers.length : 1;

    // Modularity (check for exports and imports)
    const hasExports = /export\s+(const|function|class|interface|type)/.test(code);
    const hasImports = /import\s+/.test(code);
    const modularity = (hasExports ? 0.5 : 0) + (hasImports ? 0.5 : 0);

    // Code reuse (check for function calls vs inline code)
    const functionCalls = (code.match(/\w+\s*\([^)]*\)/g) || []).length;
    const codeReuse = Math.min(1, functionCalls / (totalLines / 5));

    // Calculate score
    let score = 50;
    score += commentRatio * 20; // Up to 20 points for comments
    score += namingQuality * 15; // Up to 15 points for naming
    score += modularity * 10; // Up to 10 points for modularity
    score += codeReuse * 5; // Up to 5 points for reuse
    score = Math.min(100, Math.max(0, score));

    return {
      commentRatio: Math.round(commentRatio * 100) / 100,
      namingQuality: Math.round(namingQuality * 100) / 100,
      modularity: Math.round(modularity * 100) / 100,
      codeReuse: Math.round(codeReuse * 100) / 100,
      score: Math.round(score),
    };
  }

  /**
   * Analyze security vulnerabilities
   */
  private analyzeSecurity(code: string): SecurityMetrics {
    const vulnerabilities: SecurityVulnerability[] = [];
    const lines = code.split('\n');

    // Security patterns to check
    const securityChecks = [
      {
        pattern: /eval\s*\(/,
        type: 'code_injection',
        severity: 'critical' as const,
        message: 'eval() can execute arbitrary code - major security risk',
        fix: 'Use JSON.parse() for JSON, or Function constructor with caution',
      },
      {
        pattern: /innerHTML\s*=/,
        type: 'xss',
        severity: 'high' as const,
        message: 'innerHTML can lead to XSS attacks',
        fix: 'Use textContent or sanitize HTML with DOMPurify',
      },
      {
        pattern: /document\.write\s*\(/,
        type: 'xss',
        severity: 'high' as const,
        message: 'document.write can be exploited for XSS',
        fix: 'Use DOM manipulation methods instead',
      },
      {
        pattern: /new\s+Function\s*\(/,
        type: 'code_injection',
        severity: 'high' as const,
        message: 'Function constructor can execute arbitrary code',
        fix: 'Avoid dynamic code execution',
      },
      {
        pattern: /dangerouslySetInnerHTML/,
        type: 'xss',
        severity: 'medium' as const,
        message: 'dangerouslySetInnerHTML can lead to XSS if not sanitized',
        fix: 'Sanitize content with DOMPurify before using',
      },
      {
        pattern: /(password|secret|apiKey|api_key|token|privateKey)\s*[=:]\s*['"][^'"]{8,}['"]/i,
        type: 'hardcoded_secret',
        severity: 'critical' as const,
        message: 'Hardcoded secret detected',
        fix: 'Use environment variables for secrets',
      },
      {
        pattern: /https?:\/\/[^\s'"]+/,
        type: 'hardcoded_url',
        severity: 'low' as const,
        message: 'Hardcoded URL - may cause issues in different environments',
        fix: 'Use environment variables or config for URLs',
      },
      {
        pattern: /\bexec\s*\(/,
        type: 'command_injection',
        severity: 'critical' as const,
        message: 'exec() can lead to command injection',
        fix: 'Use execFile() or spawn() with argument arrays',
      },
      {
        pattern: /\$\{.*\}/,
        type: 'template_injection',
        severity: 'medium' as const,
        message: 'Template literal may be vulnerable to injection if using user input',
        fix: 'Sanitize user input before using in template literals',
      },
      {
        pattern: /localStorage\.setItem\s*\([^,]+,\s*[^)]*password/i,
        type: 'insecure_storage',
        severity: 'high' as const,
        message: 'Storing sensitive data in localStorage is insecure',
        fix: 'Use secure HTTP-only cookies for sensitive data',
      },
      {
        pattern: /Math\.random\s*\(\)/,
        type: 'weak_random',
        severity: 'low' as const,
        message: 'Math.random() is not cryptographically secure',
        fix: 'Use crypto.getRandomValues() for security-sensitive operations',
      },
      {
        pattern: /http:\/\//,
        type: 'insecure_protocol',
        severity: 'medium' as const,
        message: 'HTTP is insecure - use HTTPS',
        fix: 'Change to HTTPS or use protocol-relative URLs',
      },
    ];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      for (const check of securityChecks) {
        if (check.pattern.test(line)) {
          vulnerabilities.push({
            type: check.type,
            severity: check.severity,
            line: i + 1,
            message: check.message,
            fix: check.fix,
          });
        }
      }
    }

    // Calculate risk level
    const criticalCount = vulnerabilities.filter(v => v.severity === 'critical').length;
    const highCount = vulnerabilities.filter(v => v.severity === 'high').length;
    
    let riskLevel: SecurityMetrics['riskLevel'] = 'low';
    if (criticalCount > 0) riskLevel = 'critical';
    else if (highCount > 0) riskLevel = 'high';
    else if (vulnerabilities.length > 2) riskLevel = 'medium';

    // Calculate score
    let score = 100;
    score -= criticalCount * 30;
    score -= highCount * 20;
    score -= vulnerabilities.filter(v => v.severity === 'medium').length * 10;
    score -= vulnerabilities.filter(v => v.severity === 'low').length * 5;
    score = Math.max(0, score);

    return {
      vulnerabilities,
      riskLevel,
      score,
    };
  }

  /**
   * Analyze best practices
   */
  private analyzeBestPractices(code: string, language: string): BestPracticesMetrics {
    const violations: BestPracticeViolation[] = [];
    const lines = code.split('\n');

    const bestPracticeChecks = [
      // TypeScript/JavaScript
      {
        pattern: /var\s+\w+/,
        rule: 'prefer-const-let',
        severity: 'warning' as const,
        message: 'Use const or let instead of var',
        fix: 'Replace var with const (if not reassigned) or let',
      },
      {
        pattern: /==(?!=)/,
        rule: 'strict-equality',
        severity: 'warning' as const,
        message: 'Use === instead of == for strict equality',
        fix: 'Replace == with ===',
      },
      {
        pattern: /!=(?!=)/,
        rule: 'strict-inequality',
        severity: 'warning' as const,
        message: 'Use !== instead of != for strict inequality',
        fix: 'Replace != with !==',
      },
      {
        pattern: /console\.(log|warn|error|info|debug)\s*\(/,
        rule: 'no-console',
        severity: 'info' as const,
        message: 'Remove console statements in production code',
        fix: 'Use a proper logging library or remove',
      },
      {
        pattern: /debugger/,
        rule: 'no-debugger',
        severity: 'error' as const,
        message: 'Remove debugger statement',
        fix: 'Delete the debugger statement',
      },
      {
        pattern: /\bany\b/,
        rule: 'no-any',
        severity: 'warning' as const,
        message: 'Avoid using any type in TypeScript',
        fix: 'Use a more specific type or unknown',
      },
      {
        pattern: /\/\/\s*TODO/i,
        rule: 'no-todo',
        severity: 'info' as const,
        message: 'TODO comment found - consider addressing',
        fix: 'Complete the TODO or create a ticket',
      },
      {
        pattern: /\/\/\s*FIXME/i,
        rule: 'no-fixme',
        severity: 'warning' as const,
        message: 'FIXME comment found - needs attention',
        fix: 'Fix the issue or create a ticket',
      },
      {
        pattern: /\.then\s*\([^)]*\)\s*\.catch\s*\(\s*\)/,
        rule: 'empty-catch',
        severity: 'error' as const,
        message: 'Empty catch block swallows errors',
        fix: 'Handle the error or at least log it',
      },
      {
        pattern: /catch\s*\([^)]*\)\s*\{\s*\}/,
        rule: 'empty-catch-block',
        severity: 'error' as const,
        message: 'Empty catch block swallows errors',
        fix: 'Handle the error or at least log it',
      },
      {
        pattern: /async\s+\w+\s*\([^)]*\)[^{]*\{[^}]*\}/,
        rule: 'async-no-await',
        severity: 'warning' as const,
        message: 'Async function without await',
        fix: 'Remove async or add await',
      },
      {
        pattern: /setTimeout\s*\([^,]+,\s*0\s*\)/,
        rule: 'setTimeout-zero',
        severity: 'info' as const,
        message: 'setTimeout with 0 delay - consider using queueMicrotask',
        fix: 'Use queueMicrotask() for immediate async execution',
      },
      {
        pattern: /new\s+Array\s*\(/,
        rule: 'prefer-array-literal',
        severity: 'info' as const,
        message: 'Prefer array literal syntax []',
        fix: 'Use [] instead of new Array()',
      },
      {
        pattern: /new\s+Object\s*\(/,
        rule: 'prefer-object-literal',
        severity: 'info' as const,
        message: 'Prefer object literal syntax {}',
        fix: 'Use {} instead of new Object()',
      },
    ];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      for (const check of bestPracticeChecks) {
        if (check.pattern.test(line)) {
          violations.push({
            rule: check.rule,
            severity: check.severity,
            line: i + 1,
            message: check.message,
            fix: check.fix,
          });
        }
      }
    }

    // Calculate adherence score
    const totalChecks = bestPracticeChecks.length;
    const violatedRules = new Set(violations.map(v => v.rule)).size;
    const adherenceScore = Math.round(((totalChecks - violatedRules) / totalChecks) * 100);

    // Calculate score
    let score = 100;
    score -= violations.filter(v => v.severity === 'error').length * 15;
    score -= violations.filter(v => v.severity === 'warning').length * 8;
    score -= violations.filter(v => v.severity === 'info').length * 3;
    score = Math.max(0, score);

    return {
      violations,
      adherenceScore,
      score: Math.round(score),
    };
  }

  /**
   * Detect code smells
   */
  private detectCodeSmells(code: string): CodeSmell[] {
    const smells: CodeSmell[] = [];
    const lines = code.split('\n');

    // Long function detection
    let functionStart = -1;
    let braceCount = 0;
    let inFunction = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (/(?:function|=>)\s*[{(]/.test(line) && !inFunction) {
        functionStart = i;
        inFunction = true;
        braceCount = 0;
      }

      if (inFunction) {
        braceCount += (line.match(/\{/g) || []).length;
        braceCount -= (line.match(/\}/g) || []).length;

        if (braceCount <= 0 && functionStart >= 0) {
          const functionLength = i - functionStart + 1;
          if (functionLength > 50) {
            smells.push({
              type: 'long-function',
              severity: functionLength > 100 ? 'critical' : 'major',
              location: { start: functionStart + 1, end: i + 1 },
              description: `Function is ${functionLength} lines long`,
              suggestion: 'Break into smaller functions (aim for < 30 lines)',
            });
          }
          inFunction = false;
          functionStart = -1;
        }
      }

      // Long line detection
      if (line.length > 120) {
        smells.push({
          type: 'long-line',
          severity: 'minor',
          location: { start: i + 1, end: i + 1 },
          description: `Line is ${line.length} characters long`,
          suggestion: 'Break line into multiple lines (max 120 chars)',
        });
      }

      // Magic number detection
      const magicNumbers = line.match(/(?<![\w.])\d{2,}(?![\w])/g);
      if (magicNumbers) {
        for (const num of magicNumbers) {
          if (!['100', '1000', '60', '24', '365', '256', '1024'].includes(num)) {
            smells.push({
              type: 'magic-number',
              severity: 'minor',
              location: { start: i + 1, end: i + 1 },
              description: `Magic number ${num} detected`,
              suggestion: 'Extract to a named constant',
            });
          }
        }
      }

      // Deeply nested code
      const leadingSpaces = line.match(/^(\s*)/)?.[1].length || 0;
      const indentLevel = Math.floor(leadingSpaces / 2);
      if (indentLevel > 5 && line.trim()) {
        smells.push({
          type: 'deep-nesting',
          severity: 'major',
          location: { start: i + 1, end: i + 1 },
          description: `Code is nested ${indentLevel} levels deep`,
          suggestion: 'Reduce nesting with early returns or extract functions',
        });
      }

      // Duplicate code patterns (simplified)
      if (i > 0 && lines[i - 1] && line.trim() === lines[i - 1].trim() && line.trim().length > 20) {
        smells.push({
          type: 'duplicate-code',
          severity: 'major',
          location: { start: i, end: i + 1 },
          description: 'Duplicate code detected',
          suggestion: 'Extract to a shared function or variable',
        });
      }

      // God object / too many parameters
      const paramMatch = line.match(/\([^)]{100,}\)/);
      if (paramMatch) {
        smells.push({
          type: 'too-many-parameters',
          severity: 'major',
          location: { start: i + 1, end: i + 1 },
          description: 'Function has too many parameters',
          suggestion: 'Use an options object instead',
        });
      }
    }

    return smells;
  }

  /**
   * Calculate grade from score
   */
  private calculateGrade(score: number): QualityMetrics['grade'] {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  }

  /**
   * Generate quality report
   */
  generateReport(metrics: QualityMetrics): string {
    let report = `# Code Quality Report\n\n`;
    report += `**Overall Score:** ${metrics.overallScore}/100 (Grade: ${metrics.grade})\n\n`;

    report += `## Complexity\n`;
    report += `- Cyclomatic Complexity: ${metrics.complexity.cyclomaticComplexity}\n`;
    report += `- Cognitive Complexity: ${metrics.complexity.cognitiveComplexity}\n`;
    report += `- Max Nesting Depth: ${metrics.complexity.nestingDepth}\n`;
    report += `- Lines of Code: ${metrics.complexity.linesOfCode}\n`;
    report += `- Functions: ${metrics.complexity.functionsCount}\n`;
    report += `- Score: ${metrics.complexity.score}/100\n\n`;

    report += `## Maintainability\n`;
    report += `- Comment Ratio: ${(metrics.maintainability.commentRatio * 100).toFixed(1)}%\n`;
    report += `- Naming Quality: ${(metrics.maintainability.namingQuality * 100).toFixed(1)}%\n`;
    report += `- Modularity: ${(metrics.maintainability.modularity * 100).toFixed(1)}%\n`;
    report += `- Score: ${metrics.maintainability.score}/100\n\n`;

    report += `## Security\n`;
    report += `- Risk Level: ${metrics.security.riskLevel.toUpperCase()}\n`;
    report += `- Vulnerabilities: ${metrics.security.vulnerabilities.length}\n`;
    report += `- Score: ${metrics.security.score}/100\n`;
    if (metrics.security.vulnerabilities.length > 0) {
      report += `\n### Vulnerabilities Found:\n`;
      metrics.security.vulnerabilities.forEach(v => {
        report += `- [${v.severity.toUpperCase()}] Line ${v.line}: ${v.message}\n`;
      });
    }
    report += '\n';

    report += `## Best Practices\n`;
    report += `- Adherence: ${metrics.bestPractices.adherenceScore}%\n`;
    report += `- Violations: ${metrics.bestPractices.violations.length}\n`;
    report += `- Score: ${metrics.bestPractices.score}/100\n`;
    if (metrics.bestPractices.violations.length > 0) {
      report += `\n### Violations:\n`;
      metrics.bestPractices.violations.slice(0, 10).forEach(v => {
        report += `- [${v.severity.toUpperCase()}] Line ${v.line}: ${v.message}\n`;
      });
    }
    report += '\n';

    if (metrics.codeSmells.length > 0) {
      report += `## Code Smells (${metrics.codeSmells.length})\n`;
      metrics.codeSmells.slice(0, 10).forEach(s => {
        report += `- [${s.severity.toUpperCase()}] ${s.type}: ${s.description}\n`;
        report += `  Suggestion: ${s.suggestion}\n`;
      });
    }

    return report;
  }
}

export function createCodeQualityAnalyzer(workspaceRoot: string): CodeQualityAnalyzer {
  return new CodeQualityAnalyzer(workspaceRoot);
}
